import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Upload, Download, Eye, Trash2, Loader2, FileText, Image as ImageIcon, Globe2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";

const DOCS = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.odt,.ods,.odp,.rtf";
const IMGS = "image/*,.heic,.heif";
const VIDEO = "video/mp4,video/quicktime,video/webm,.m4v";
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const isImg = (a) => (a.content_type || "").startsWith("image/");
const isPdf = (a) => a.content_type === "application/pdf" || /\.pdf$/i.test(a.nome);
const monthNow = () => new Date().toISOString().slice(0, 7);
const fmtMese = (m) => (m ? new Date(`${m}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : "");

const blobOf = async (id, inline) => (await api.get(`/allegati/${id}/download`, { params: inline ? { inline: 1 } : {}, responseType: "blob" })).data;

const Row = ({ a, readOnly, ped, onPreview, onDelete, onPortal }) => {
  const download = async () => {
    try { const url = URL.createObjectURL(await blobOf(a.id)); const l = document.createElement("a"); l.href = url; l.download = a.nome; l.click(); setTimeout(() => URL.revokeObjectURL(url), 4000); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-white/80 border border-[#5FB3EA]/60 px-3 py-2" data-testid={`att-row-${a.id}`}>
      {isImg(a) ? <ImageIcon className="h-4 w-4 text-[#E8173A] shrink-0" /> : <FileText className="h-4 w-4 text-[#0A1128] shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-[#0A1128] truncate">{a.nome}{ped && <span className="ml-2 rounded-full bg-[#0A1128] px-2 py-0.5 text-[10px] font-bold text-white align-middle">PED</span>}</div>
        <div className="text-[11px] text-slate-500 truncate">{size(a.size)}{a.mese ? ` · ${fmtMese(a.mese)}` : ""}{a.uploaded_by_nome ? ` · ${a.uploaded_by_nome}` : ""}{a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString("it-IT")}` : ""}{a.note ? ` · ${a.note}` : ""}</div>
      </div>
      {onPortal && (
        <label className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A1128]" title="Visibile al cliente nel portale">
          <Switch checked={a.portale} onCheckedChange={(v) => onPortal(a, v)} className="data-[state=checked]:bg-[#E8173A]" data-testid={`att-portal-${a.id}`} />
          <Globe2 className="h-3.5 w-3.5" />Portale
        </label>
      )}
      {(isImg(a) || isPdf(a)) && <Button type="button" variant="ghost" size="sm" onClick={() => onPreview(a)} aria-label="Anteprima" data-testid={`att-preview-${a.id}`}><Eye className="h-4 w-4" /></Button>}
      <Button type="button" variant="ghost" size="sm" onClick={download} aria-label="Scarica" data-testid={`att-download-${a.id}`}><Download className="h-4 w-4" /></Button>
      {!readOnly && <Button type="button" variant="ghost" size="sm" onClick={() => onDelete(a)} aria-label="Elimina" data-testid={`att-delete-${a.id}`}><Trash2 className="h-4 w-4 text-[#E8173A]" /></Button>}
    </div>
  );
};

export const AttachmentsPanel = forwardRef(({ tipo, targetId, clientId, title = "Allegati", video = false, readOnly = false, ped = false, mese, testid = "attachments" }, ref) => {
  const [items, setItems] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [del, setDel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ mese: monthNow(), note: "" });
  const input = useRef(null);
  const ready = tipo === "ped" ? !!clientId : !!targetId;

  const load = useCallback(async () => {
    if (!ready) { setItems([]); return; }
    try { setItems((await api.get("/allegati", { params: { tipo, target_id: targetId, client_id: tipo === "ped" ? clientId : undefined, mese: mese || undefined } })).data); }
    catch { setItems([]); }
  }, [tipo, targetId, clientId, mese, ready]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (mese) setForm((f) => ({ ...f, mese })); }, [mese]);

  const send = async (files, id) => {
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("tipo", tipo); fd.append("file", file);
      if (id) fd.append("target_id", id);
      if (tipo === "ped") { fd.append("client_id", clientId); if (form.mese) fd.append("mese", form.mese); fd.append("note", form.note); }
      try { await api.post("/allegati", fd); } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    await load();
  };
  const add = (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    if (!ready) { setPending((p) => [...p, ...files]); return; }
    send(files, targetId).then(() => toast.success(files.length > 1 ? `${files.length} allegati caricati` : "Allegato caricato"));
  };
  const flush = async (id) => { if (pending.length) { const f = pending; setPending([]); await send(f, id); } };
  useImperativeHandle(ref, () => ({ flush }));
  useEffect(() => { if (targetId && pending.length) flush(targetId); }, [targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreview = async (a) => {
    try { setPreview({ a, url: URL.createObjectURL(await blobOf(a.id, true)) }); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/allegati/${del.id}`); setItems((l) => l.filter((x) => x.id !== del.id)); toast.success("Allegato eliminato"); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  const portal = async (a, v) => {
    try { const r = await api.patch(`/allegati/${a.id}`, { portale: v }); setItems((l) => l.map((x) => (x.id === a.id ? r.data : x))); toast.success(v ? "Visibile al cliente nel portale" : "Nascosto dal portale"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const accept = `${IMGS},${DOCS}${video ? `,${VIDEO}` : ""}`;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-600"><Paperclip className="h-3.5 w-3.5" />{title}{!!items?.length && <span className="rounded-full bg-[#0A1128] px-2 text-[10px] text-white" data-testid={`${testid}-count`}>{items.length}</span>}</div>
      {!readOnly && tipo === "ped" && (
        <div className="grid gap-2 sm:grid-cols-[180px_1fr]">
          <Input type="month" value={form.mese} onChange={(e) => setForm({ ...form, mese: e.target.value })} className="rounded-xl bg-white" aria-label="Mese di riferimento" data-testid={`${testid}-mese`} />
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Nota (facoltativa)" className="rounded-xl bg-white" data-testid={`${testid}-note`} />
        </div>
      )}
      {!readOnly && (
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
          onClick={() => input.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && input.current?.click()}
          className={`flex min-h-[64px] cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-3 text-center text-sm font-semibold transition-colors ${drag ? "border-[#E8173A] bg-[#E8173A]/5" : "border-[#5FB3EA] bg-white/50 hover:border-[#0A1128]"}`}
          data-testid={`${testid}-dropzone`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>Trascina qui o tocca per scegliere: foto, PDF, Office, zip{video ? ", video" : ""} <span className="text-slate-500 font-normal">(max 25 MB{video ? ", video 100 MB" : ""})</span></span>
          <input ref={input} type="file" multiple accept={accept} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
        </div>
      )}
      {!!pending.length && <div className="text-xs font-semibold text-[#0A1128]" data-testid={`${testid}-pending`}>{pending.length} file in attesa: verranno caricati al salvataggio ({pending.map((f) => f.name).join(", ")})</div>}
      <div className="grid gap-1.5">
        {(items || []).map((a) => <Row key={a.id} a={a} readOnly={readOnly} ped={ped} onPreview={openPreview} onDelete={setDel} onPortal={tipo === "ped" && !readOnly ? portal : null} />)}
        {items && !items.length && readOnly && <div className="text-xs text-slate-500">Nessun file.</div>}
      </div>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare l'allegato?" description={`«${del?.nome}» verrà eliminato.`} onConfirm={remove} />
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { URL.revokeObjectURL(preview?.url); setPreview(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="att-preview-dialog">
          <DialogHeader><DialogTitle className="truncate">{preview?.a.nome}</DialogTitle></DialogHeader>
          {preview && (isImg(preview.a) ? <img src={preview.url} alt={preview.a.nome} className="max-h-[75vh] w-full object-contain rounded-xl" />
            : <iframe src={preview.url} title={preview.a.nome} className="h-[75vh] w-full rounded-xl border" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
});
AttachmentsPanel.displayName = "AttachmentsPanel";

export const useAttachmentCounts = (tipo, deps = []) => {
  const [map, setMap] = useState({});
  useEffect(() => { api.get("/allegati/conteggi", { params: { tipo } }).then((r) => setMap(r.data)).catch(() => {}); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return map;
};
